import React, { useState, useMemo, useEffect } from 'react';
import { useSelector } from 'react-redux';
import styled from '@emotion/styled/macro';
import { Formik, Form } from 'formik';
import { Heading, Box, SimpleGrid, Text, useDisclosure } from '@chakra-ui/react';
import { FormattedMessage, useIntl } from 'react-intl';
import { ReactComponent as AddIcon } from 'icons/add-item.svg';

import { applyGenericAssetTemplate } from 'services/autoforms';
import { applyGenericAssetTemplateItemType } from 'services/items';

import ComboboxFormikField from '../forms/ComboboxFormikField';
import InputFormikField from '../forms/InputFormikField';
import AutomaticFormikDirtyCheck from '../forms/AutomaticFormikDirtyCheck';
import useItemCreationWizard from 'hooks/useItemCreationWizard';
import NavigationButtons from './CustomSubItemCreation/NavigationButtons';
import SecondaryButton from '../utils/SecondaryButton';
import AddWeatherZoneModal from 'components/itemCreationWizard/CustomSubItemCreation/WeatherZone/AddWeatherZoneModal';

const GenericAssetWorkflowStep = () => {
    const intl = useIntl();
    const createWeatherZoneModal = useDisclosure();

    const [isFormDirty, setIsFormDirty] = useState(false);
    const removeFileExtensionRegex = /\.[^/.]+$/;

    const {
        itemTypeId: selectedItemTypeId,
        autoForm: selectedAutoForm,
        itemTypeName,
        newItemId,
        genericTemplates,
        moveToSubItemDecisionStep,
        moveToGenericItemPage,
        onFormHasUnsavedChanges,
        availableWeatherZones,
        updateAvailableWeatherZones,
        weatherZoneAssignableItemTypeIds,
    } = useItemCreationWizard();

    const rescalingSupportedItemTypes = useSelector((state) => state.item.rescalingSupportedItemTypes);
    const isRescalingSupported = rescalingSupportedItemTypes.some((item) => item.itemTypeId === selectedItemTypeId);

    useEffect(() => {
        onFormHasUnsavedChanges(isFormDirty);
    }, [isFormDirty, onFormHasUnsavedChanges]);

    useEffect(() => {
        if (!Array.isArray(availableWeatherZones) || availableWeatherZones.length === 0) {
            updateAvailableWeatherZones();
        }
    }, [availableWeatherZones, updateAvailableWeatherZones]);

    const displayWeatherZone = useMemo(() => {
        return weatherZoneAssignableItemTypeIds?.includes(selectedItemTypeId) ?? false;
    }, [weatherZoneAssignableItemTypeIds, selectedItemTypeId]);

    const handleSubmit = async (formValues) => {
        let timeoutHit = false;
        const timeout = new Promise((resolve) =>
            setTimeout(() => {
                timeoutHit = true;
                resolve();
            }, 10000)
        );

        //apply template but move on if it takes too long
        const applyTemplate = async () => {
            if (selectedAutoForm !== null && selectedAutoForm !== '') {
                await applyGenericAssetTemplate(selectedAutoForm, newItemId, formValues.templates);
            } else {
                const scalingValue = isRescalingSupported ? formValues.scalingValue : 1;
                await applyGenericAssetTemplateItemType(
                    selectedItemTypeId,
                    newItemId,
                    formValues.templates,
                    formValues.weatherZoneId,
                    scalingValue
                );
            }
        };
        await Promise.race([applyTemplate(), timeout]).then(() => {
            onFormHasUnsavedChanges(false);
            moveToGenericItemPage(timeoutHit);
        });
    };

    const handlePreviousStep = () => {
        moveToSubItemDecisionStep();
    };

    const initialFormValues = useMemo(
        () => ({
            templates: genericTemplates,
        }),
        [genericTemplates]
    );

    const formLabels = useMemo(
        () => ({
            templates: intl.formatMessage({ id: 'add_item_starter_data_template_dropdown' }),
            weatherZone: intl.formatMessage({ id: 'renewable_definitions_weather_zone' }),
            createWeatherZone: intl.formatMessage({ id: 'item_creation_wizard_weatherZone_heading' }),
            selectPlaceholder: intl.formatMessage({ id: 'common_forms_select_option' }),
        }),
        [intl]
    );

    const validateScalingValue = (value) => {
        if (value !== undefined && value !== '') {
            if (value <= 0) {
                return intl.formatMessage({ id: 'rescaling_scalingValue_input_error' });
            }
        }
    };

    return (
        <>
            <Formik
                initialValues={initialFormValues}
                onSubmit={handleSubmit}
                enableReinitialize={true}
                validateOnChange={true}
                validateOnBlur={false}
            >
                {({ isSubmitting, errors, touched, values }) => (
                    <Form>
                        <AutomaticFormikDirtyCheck isFormDirty={isFormDirty} onFormDirtyChange={setIsFormDirty} />
                        <CenteredWrapper>
                            <Heading pt={7} pb={0}>
                                <FormattedMessage
                                    id="item_creation_wizard_generic_asset_heading"
                                    values={{ itemTypeName: itemTypeName }}
                                />
                            </Heading>
                            <AutomaticFormikDirtyCheck isFormDirty={isFormDirty} onFormDirtyChange={setIsFormDirty} />

                            <SimpleGrid pt={7} pb={0} columns={{ xl: 4 }} spacingX={6} spacingY={4}>
                                <ComboboxFormikField
                                    id="templates"
                                    name="templates"
                                    label={formLabels.templates}
                                    options={genericTemplates.flatMap((x) =>
                                        x?.fileName?.replace(removeFileExtensionRegex, '')
                                    )}
                                    isFastField
                                    isRequired
                                />
                                {isRescalingSupported && (
                                    <InputFormikField
                                        id="scalingValue"
                                        name="scalingValue"
                                        type="number"
                                        label={<FormattedMessage id="rescaling_input_label" />}
                                        info={<FormattedMessage id="rescaling_input_tooltip" />}
                                        isDisabled={!isRescalingSupported}
                                        isRequired={false}
                                        isFastField
                                        validate={validateScalingValue}
                                        step="any"
                                    ></InputFormikField>
                                )}
                            </SimpleGrid>
                            {displayWeatherZone && (
                                <SimpleGrid pt={7} pb={0} columns={{ xl: 4 }} spacingX={6} spacingY={4}>
                                    <ComboboxFormikField
                                        id="weatherZoneId"
                                        name="weatherZoneId"
                                        label={formLabels.weatherZone}
                                        isRequired={displayWeatherZone}
                                        options={availableWeatherZones}
                                        labelKey="name"
                                        valueKey="id"
                                        placeholderValue=""
                                        placeholderLabel={formLabels.selectPlaceholder}
                                        width="100%"
                                        isDisabled={!displayWeatherZone}
                                    />
                                    <Box>
                                        <Text size="xs" letterSpacing="0.5px" mb={1}>
                                            <FormattedMessage id="item_creation_wizard_weatherZone_heading" />
                                        </Text>
                                        <SecondaryButton
                                            variant="secondary"
                                            size="sm"
                                            onClick={createWeatherZoneModal.onOpen}
                                            width="50%"
                                            leftIcon={<AddIcon />}
                                        >
                                            <Box as="span" textTransform="capitalize">
                                                <FormattedMessage id="common_add_new" />
                                            </Box>
                                        </SecondaryButton>
                                    </Box>
                                </SimpleGrid>
                            )}
                        </CenteredWrapper>
                        <NavigationButtons
                            isPrevStepDisabled={isSubmitting}
                            isConfirmSubmitting={isSubmitting}
                            isConfirmDisabled={isSubmitting}
                            handlePreviousStep={handlePreviousStep}
                        />
                    </Form>
                )}
            </Formik>

            {createWeatherZoneModal.isOpen && (
                <AddWeatherZoneModal
                    isOpen
                    weatherZones={availableWeatherZones}
                    onClose={createWeatherZoneModal.onClose}
                />
            )}
        </>
    );
};

const CenteredWrapper = styled(Box)`
    width: 100%;
    max-width: 1314px;
    margin: 0 auto;
    padding: 0 16px;
`;

export default GenericAssetWorkflowStep;
